import React, { useEffect, useState } from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    CircularProgress,
    Paper,
    Grid,
    Fade,
    IconButton,
    Avatar,
    Snackbar,
    Alert,
    useTheme,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useLanguage } from './LanguageContext';

export default function TranscriptionPage() {
    const { t } = useLanguage();
    const { transcription_id } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();

    const [loading, setLoading] = useState(true);
    const [transcription, setTranscription] = useState(null);
    const [setErrorMsg] = useState('');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'error'
    });

    useEffect(() => {
        document.title = `${t.transcription} | ClassCapture`;
    }, [t]);

    useEffect(() => {
        const fetchTranscription = async () => {
            try {
                const auth = getAuth();
                const token = await auth.currentUser.getIdToken();
                const url = `https://transcribeservices-470732867211.us-central1.run.app/api/transcribe/${transcription_id}`;
                const response = await fetch(url, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!response.ok) new Error('fetch_failed');
                const data = await response.json();
                setTranscription(data.transcription);
            } catch {
                const msg = t.errorFetchingTranscription;
                setErrorMsg(msg);
                setSnackbar({ open: true, message: msg, severity: 'error' });
            } finally {
                setLoading(false);
            }
        };
        fetchTranscription();
    }, [transcription_id, t, setErrorMsg]);

    const handleSnackbarClose = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(s => ({ ...s, open: false }));
    };

    const copyToClipboard = () => {
        const text = transcription?.content?.cleanText;
        if (text) {
            navigator.clipboard.writeText(text)
                .then(() => setSnackbar({ open: true, message: t.textCopied, severity: 'success' }))
                .catch(() => setSnackbar({ open: true, message: t.copyFailed, severity: 'error' }));
        }
    };

    const gradientBackground = {
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
    };

    if (loading) {
        return (
            <Container sx={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                height: '100vh', flexDirection: 'column', gap: 1
            }}>
                <DescriptionIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                <Typography variant="subtitle1" color="primary" fontWeight={450}>
                    ClassCapture
                </Typography>
                <CircularProgress color="primary" size={24} />
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="lg" sx={{ py: 2 }}>
            <Fade in timeout={800}>
                <Paper elevation={1} sx={{
                    p: 2, mb: 2, borderRadius: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    ...gradientBackground, color: 'white'
                }}>
                    <Box sx={{ color:'black',display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            color="inherit"
                            onClick={() => navigate(-1)}
                            sx={{ mr: 1 }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" component="h1" fontWeight={450}>
                            {transcription?.metadata?.title || t.transcription}
                        </Typography>
                    </Box>

                    {transcription && (
                        <Button
                            variant="outlined"
                            size="small"
                            color='inherit'
                            startIcon={<ContentCopyIcon />}
                            onClick={copyToClipboard}
                            sx={{
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': {
                                    borderColor: 'white',
                                    bgcolor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                        >
                            {t.copyText}
                        </Button>
                    )}
                </Paper>
            </Fade>

            {transcription ? (
                <Fade in timeout={1000}>
                    <Box>
                        {/* Metadata */}
                        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 1 }}>
                            <Grid container spacing={2}>
                                {transcription.timestamps.updatedAt && (
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Avatar sx={{
                                                bgcolor: theme.palette.primary.light,
                                                width: 32, height: 32, mr: 1
                                            }}>
                                                <CalendarTodayIcon sx={{ fontSize: 16 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t.dateLabel}
                                                </Typography>
                                                <Typography variant="body2" fontWeight={450}>
                                                    {new Date(transcription.timestamps.updatedAt).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                )}
                                {transcription.metadata.description && (
                                    <Grid item xs={12} sm={6} md={8}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Avatar sx={{
                                                bgcolor: theme.palette.info.light,
                                                width: 32, height: 32, mr: 1
                                            }}>
                                                <DescriptionIcon sx={{ fontSize: 16 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t.descriptionLabel}
                                                </Typography>
                                                <Typography variant="body2" fontWeight={450}>
                                                    {transcription.metadata.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>

                        {/* Content */}
                        <Paper elevation={1} sx={{ borderRadius: 1, overflow: 'hidden' }}>
                            <Box sx={{
                                p: 1.5,
                                bgcolor: theme.palette.grey[100],
                                borderBottom: `1px solid ${theme.palette.divider}`
                            }}>
                                <Typography variant="subtitle2" fontWeight={450}>
                                    {t.contentLabel}
                                </Typography>
                            </Box>
                            <Box sx={{
                                p: 3,
                                maxHeight: '60vh',
                                overflowY: 'auto',
                                bgcolor: 'white'
                            }}>
                                <Typography variant="body1" sx={{
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.8,
                                    color: theme.palette.text.primary
                                }}>
                                    {transcription.content.cleanText}
                                </Typography>
                            </Box>
                        </Paper>
                    </Box>
                </Fade>
            ) : (
                <Fade in timeout={1000}>
                    <Paper elevation={1} sx={{
                        p: 3, borderRadius: 1,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', gap: 2
                    }}>
                        <DescriptionIcon color="error" sx={{ fontSize: 48 }} />
                        <Typography variant="h6" color="error" fontWeight={450}>
                            {t.transcriptionNotFound}
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(-1)}
                        >
                            {t.goBack}
                        </Button>
                    </Paper>
                </Fade>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%', py: 0.5 }}
                    elevation={2}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}
